import { useState } from 'react';

// Order matches the "Cuota actual" section of the E3 extension brief: Plan,
// Inicio, Vencimiento, Precio, Saldo. All five are optional — the service
// only migrates a period when `planName` resolves to a non-empty column
// (member-import-service.js's `readQuota`); the rest can be left "No
// importar" and the member is still created/updated without a period.
const QUOTA_FIELDS = [
  { key: 'planNameCol', label: 'Plan', id: 'gym-import-map-plan' },
  { key: 'periodStartCol', label: 'Inicio', id: 'gym-import-map-inicio' },
  { key: 'periodEndCol', label: 'Vencimiento', id: 'gym-import-map-vencimiento' },
  { key: 'priceCol', label: 'Precio', id: 'gym-import-map-precio' },
  { key: 'balanceCol', label: 'Saldo', id: 'gym-import-map-saldo' },
];

function splitName(nameMapping) {
  if (Array.isArray(nameMapping)) return { nombreCol: nameMapping[0] || '', apellidoCol: nameMapping[1] || '' };
  return { nombreCol: nameMapping || '', apellidoCol: '' };
}

function fieldsFromMapping(mapping) {
  const source = mapping || {};
  const { nombreCol, apellidoCol } = splitName(source.name);
  return {
    nombreCol,
    apellidoCol,
    emailCol: source.email || '',
    documentNumberCol: source.documentNumber || '',
    phoneCol: source.phone || '',
    planNameCol: source.planName || '',
    periodStartCol: source.periodStart || '',
    periodEndCol: source.periodEnd || '',
    priceCol: source.price || '',
    balanceCol: source.balance || '',
  };
}

function ColumnSelect({
  id, label, value, columns, onChange, disabled, required,
}) {
  return (
    <div className="gym-field gym-import-mapping__field">
      <label htmlFor={id} className="gym-field__label">{label}{required ? '' : ' (opcional)'}</label>
      <select id={id} className="gym-input" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        <option value="">No importar</option>
        {columns.map((column) => <option key={column} value={column}>{column}</option>)}
      </select>
    </div>
  );
}

/**
 * Step 2 of the import wizard (plan task E6): map the uploaded file's
 * columns to the fields `POST /members/import/preview`/`commit` expect
 * (`member-import-service.js`'s `MAPPING_FIELDS`), preselected from the
 * server's own `suggestedMapping`. `initialMapping` is whatever the admin
 * last submitted (or the server's suggestion on the very first pass) — going
 * "volver" from the preview step and back here never loses their choices.
 *
 * The optional "Cuota actual" section only matters when a Plan column is
 * chosen; leaving every quota field on "No importar" migrates members
 * without a period, exactly like the API's own contract.
 */
export function MappingStep({
  columns, sampleRows, initialMapping, onSubmit, onCancel, submitting, error,
}) {
  const [fields, setFields] = useState(() => fieldsFromMapping(initialMapping));
  const [localError, setLocalError] = useState('');

  function set(key) {
    return (value) => setFields((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    if (!fields.nombreCol) {
      setLocalError('Elegí la columna de nombre.');
      return;
    }
    if (!fields.emailCol) {
      setLocalError('Elegí la columna de email.');
      return;
    }
    setLocalError('');
    onSubmit({
      name: fields.apellidoCol ? [fields.nombreCol, fields.apellidoCol] : fields.nombreCol,
      email: fields.emailCol,
      documentNumber: fields.documentNumberCol || null,
      phone: fields.phoneCol || null,
      planName: fields.planNameCol || null,
      periodStart: fields.periodStartCol || null,
      periodEnd: fields.periodEndCol || null,
      price: fields.priceCol || null,
      balance: fields.balanceCol || null,
    });
  }

  const shownError = localError || error;

  return (
    <form className="gym-form gym-card gym-import-mapping" onSubmit={handleSubmit} noValidate>
      {shownError && <p role="alert" className="gym-form__error">{shownError}</p>}

      <div className="gym-import-mapping__grid">
        <ColumnSelect id="gym-import-map-nombre" label="Nombre" required value={fields.nombreCol} columns={columns} disabled={submitting} onChange={set('nombreCol')} />
        <ColumnSelect id="gym-import-map-apellido" label="Apellido" value={fields.apellidoCol} columns={columns} disabled={submitting} onChange={set('apellidoCol')} />
        <ColumnSelect id="gym-import-map-email" label="Email" required value={fields.emailCol} columns={columns} disabled={submitting} onChange={set('emailCol')} />
        <ColumnSelect id="gym-import-map-dni" label="DNI" value={fields.documentNumberCol} columns={columns} disabled={submitting} onChange={set('documentNumberCol')} />
        <ColumnSelect id="gym-import-map-telefono" label="Teléfono" value={fields.phoneCol} columns={columns} disabled={submitting} onChange={set('phoneCol')} />
      </div>

      <fieldset className="gym-import-mapping__quota">
        <legend>Cuota actual (opcional)</legend>
        <p className="gym-muted-text">
          Si elegís un Plan, también se va a crear el período con su precio y saldo actuales.
        </p>
        <div className="gym-import-mapping__grid">
          {QUOTA_FIELDS.map(({ key, label, id }) => (
            <ColumnSelect key={key} id={id} label={label} value={fields[key]} columns={columns} disabled={submitting} onChange={set(key)} />
          ))}
        </div>
      </fieldset>

      <div>
        <h2 className="gym-import-mapping__sample-title">Vista de muestra</h2>
        <div className="gym-import-table-wrapper">
          <table className="gym-table gym-import-sample-table" aria-label="Muestra de filas del archivo">
            <thead>
              <tr>{columns.map((column) => <th key={column} scope="col">{column}</th>)}</tr>
            </thead>
            <tbody>
              {(sampleRows || []).map((row, index) => (
                // eslint-disable-next-line react/no-array-index-key -- sample rows have no stable id
                <tr key={index}>
                  {columns.map((column) => <td key={column} data-label={column}>{row[column]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="gym-admin-form-actions">
        <button type="submit" className="gym-button gym-button--primary" disabled={submitting}>Continuar</button>
        <button type="button" className="gym-button gym-button--secondary" disabled={submitting} onClick={onCancel}>Volver</button>
      </div>
    </form>
  );
}
